import { ColorStatus } from '../types';

/**
 * CareDaily's normative comparison, published per resident in
 * `trends_metadata.<key>.normalized`. The bands are age and sex specific, so
 * the same metric returns different edges for different residents.
 */
export type NormativeBand = 'below_average' | 'normal' | 'above_average';

export interface NormativeBandBounds {
  min?: number;
  max?: number;
}

export interface NormalizedMeta {
  source?: string;
  direction?: 'higher_better' | 'lower_better';
  age_band?: string;
  sex?: string;
  normative_range?: Partial<Record<NormativeBand, NormativeBandBounds>>;
}

export interface BandColorConfig {
  aboveAverage?: ColorStatus | null;
  normal?: ColorStatus | null;
  belowAverage?: ColorStatus | null;
}

/**
 * Defaults chosen with the client: being exactly average is good news, so
 * `normal` is a calm positive color, never yellow. Only below average gets a
 * warm color, and it is amber rather than red because a fitness norm is a
 * comparison, not a clinical alarm.
 */
export const DEFAULT_BAND_COLORS: Record<keyof BandColorConfig, ColorStatus> = {
  aboveAverage: 'green',
  normal: 'blue',
  belowAverage: 'amber',
};

const BAND_LABELS: Record<NormativeBand, string> = {
  above_average: 'Above average',
  normal: 'Typical for your age',
  below_average: 'Below average',
};

const ALL_BANDS: NormativeBand[] = ['below_average', 'normal', 'above_average'];

/**
 * All three bands must be present with at least one real bound each.
 *
 * A partial range is worse than none: the same metric would color one resident
 * from CareDaily's norms and another from the facility thresholds, depending
 * only on where their score fell. All three or fall back.
 */
export function hasUsableNorms(normalized: unknown): normalized is NormalizedMeta {
  if (!normalized || typeof normalized !== 'object') return false;
  const range = (normalized as NormalizedMeta).normative_range;
  if (!range || typeof range !== 'object') return false;
  return ALL_BANDS.every(b => {
    const bounds = range[b];
    return !!bounds && (typeof bounds.min === 'number' || typeof bounds.max === 'number');
  });
}

/** True when `value` sits inside this band's interval, both edges inclusive. */
function within(value: number, bounds?: NormativeBandBounds): boolean {
  if (!bounds) return false;
  const { min, max } = bounds;
  if (typeof min !== 'number' && typeof max !== 'number') return false;
  if (typeof min === 'number' && value < min) return false;
  if (typeof max === 'number' && value > max) return false;
  return true;
}

/**
 * The band a value falls in, or null when there is nothing trustworthy to say.
 *
 * Two things this must get right, both seen in the real CareDaily payload:
 *
 * 1. `lower_better` metrics flip the shape - 8-foot up and go has
 *    `below_average: { min: 5.7 }` and `above_average: { max: 4.3 }`. So we
 *    match on the interval and never on the band's name.
 * 2. Band edges are shared - chair stand is below to 12, normal 12 to 18,
 *    above from 18, so 12 and 18 each match two bands. CareDaily's rule
 *    (Destry, 2026-09-17): "Use normal when there is overlap (inclusive)." So
 *    normal owns both of its edges, and this must agree with the verdict they
 *    publish, or a history bar could disagree with the card's label.
 *
 * If two bands match and neither is normal (below and above touching with no
 * normal between them), the data is malformed, so return null and let the card
 * fall back to thresholds rather than guess.
 */
export function resolveBand(value: number, normalized: NormalizedMeta | null | undefined): NormativeBand | null {
  if (typeof value !== 'number' || !isFinite(value)) return null;
  if (!hasUsableNorms(normalized)) return null;

  const range = normalized.normative_range!;
  const matches = ALL_BANDS.filter(b => within(value, range[b]));
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0];
  return matches.includes('normal') ? 'normal' : null;
}

export function bandToColorStatus(band: NormativeBand, colors?: BandColorConfig): ColorStatus {
  const chosen = band === 'above_average'
    ? colors?.aboveAverage
    : band === 'normal'
      ? colors?.normal
      : colors?.belowAverage;

  if (chosen) return chosen;

  return band === 'above_average'
    ? DEFAULT_BAND_COLORS.aboveAverage
    : band === 'normal'
      ? DEFAULT_BAND_COLORS.normal
      : DEFAULT_BAND_COLORS.belowAverage;
}

export function bandLabel(band: NormativeBand): string {
  return BAND_LABELS[band];
}
